import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useSubscriptionQueries from '@dashboard/hooks/queries/useSubscriptionQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import Spinner from '@dashboard/components/Spinner';
import SubscriptionItem from '@dashboard/components/SubscriptionItem';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';

const propTypes = {
  sales: PropTypes.arrayOf(PropTypes.object),
  getNext: PropTypes.func.isRequired,
};

const defaultProps = {
  sales: false,
};

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  EMAIL_ASCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Email A-Z',
  },
  EMAIL_DESCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Email Z-A',
  },
};

const FILTER_OPTIONS = {
  ALL: {
    label: 'All',
    value: 'all',
  },
  ACTIVE: {
    label: 'Active',
    value: 'active',
  },
  RECOVERED: {
    label: 'Recovered',
    value: 'recovered',
  },
  TRIALING: {
    label: 'Trialing',
    value: 'trialing',
  },
  SCHEDULED: {
    label: 'Scheduled',
    value: 'scheduled',
  },
  COMPLETED: {
    label: 'Completed',
    value: 'completed',
  },
  CANCELED: {
    label: 'Canceled',
    value: 'canceled',
  },
  PAST_DUE: {
    label: 'Overdue',
    value: 'past_due',
  },
  INCOMPLETE: {
    label: 'Incomplete',
    value: 'incomplete',
  },
  FAILED: {
    label: 'Failed',
    value: 'failed',
  },
  ABANDONED: {
    value: 'abandoned',
    label: 'Abandoned',
  },
};

function getSortOptionFromQuery(sortBy, sortDirection) {
  const sortOption = Object.keys(SORT_OPTIONS).find(
    key => SORT_OPTIONS[key].value === sortBy && SORT_OPTIONS[key].direction === sortDirection,
  );

  return sortOption || Object.keys(SORT_OPTIONS)[0];
}

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );

  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

const PaymentsSubscriptions = () => {
  const history = useHistory();
  const query = useUrlQuery();

  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));
  const [page, setPage] = useState(query.page || undefined);

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  useEffect(() => {
    setPage(query.page || undefined);
  }, [query.page]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
  };

  const filter = {
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    livemode: true,
    page: page || undefined,
  };

  useEffect(() => {
    history.replace({
      search: stringify({
        ...query,
        sortBy: sortBy,
        sortDirection: sortDirection,
        status: FILTER_OPTIONS[status].value,
        search: search,
        page: page,
      }),
    });
  }, [sortBy, sortDirection, status, search, page]);

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Subscriptions</CardHeaderTitle>

        <div className="w-full flex flex-col lg:flex-row gap-x-4 gap-y-2 lg:items-end lg:justify-between">
          <div className="w-full max-w-96">
            <PageFilterInput
              name="page"
              pageTypes={['checkout']}
              productTypes={['subscription']}
              onChange={setPage}
              initialValue={query.page}
              placeholder="Filter by checkout"
            />
          </div>

          <CardHeaderActions>
            <SearchInput
              onChange={setSearch}
              name="search_subscriptions"
              placeholder="Search by email or order ID"
              initialValue={search}
            />

            <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />

            <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

            <div>
              <ExportView
                collection="subscriptions"
                label="subscriptions"
                filters={{
                  ...filter,
                  sort: JSON.stringify(sort),
                  search,
                }}
              />
            </div>
          </CardHeaderActions>
        </div>
      </CardHeader>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="3xl">Customer</CardListItemElement>
          <CardListItemElement size="xl">Amount</CardListItemElement>
          <CardListItemElement size="4xl">Page</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <PaymentsSubscriptionsList sort={sort} search={search} filter={filter} />
      </CardContent>
    </Card>
  );
};

PaymentsSubscriptions.propTypes = propTypes;
PaymentsSubscriptions.defaultProps = defaultProps;

export function PaymentsSubscriptionsList({
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
}) {
  const { sellerId } = useAuth();

  const { getInfiniteSubscriptions } = useSubscriptionQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['subscriptions', sellerId, sort, search, filter, refreshKey],
    queryFn: async ({ pageParam }) =>
      await getInfiniteSubscriptions(sellerId, {
        ...filter,
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!infinite) return undefined;

      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return (
      <CardContent variant="root">
        <Spinner />
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent variant="root">
        Unable to show subscriptions. Please refresh or contact support.
      </CardContent>
    );
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  // handle empty state with search
  if (isEmpty && search) {
    return (
      <EmptyState>
        <EmptyState.Title>No subscriptions found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isEmpty && filter.livemode !== false) {
    return (
      <EmptyState>
        <EmptyState.Title>No subscriptions yet</EmptyState.Title>
        <EmptyState.Subtitle>Your subscriptions will show up here</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isEmpty && filter.livemode === false) {
    return (
      <EmptyState>
        <EmptyState.Subtitle>Your test subscriptions will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;

  return (
    <CardContent>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(subscription => (
            <SubscriptionItem
              subscription={subscription}
              key={subscription._id}
              options={options}
              isAbandoned={subscription.isAbandoned}
            />
          )),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="mx-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more subscriptions to show</div>
      )}
    </CardContent>
  );
}

export default PaymentsSubscriptions;
